import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { CloseOutlined, InboxOutlined } from "@ant-design/icons";

import Button from "../Button/Button";

import styles from "./FileDropzone.module.css";

/** Why a file was rejected — so consumers can show the right error message. */
export type FileRejectReason = "type" | "size";

export type FileDropzoneProps = {
  /** Currently selected file, or `null` when empty. Controlled. */
  value: File | null;
  /** Fired with the new selection: a new `File`, or `null` when removed. */
  onChange: (file: File | null) => void;

  /**
   * Comma-separated allow-list passed to the native input and used to
   * validate drops too — so a user can't bypass the picker filter by
   * dragging an unrelated file. Supports the standard `accept` grammar:
   *
   * - MIME types: `"application/pdf"`, `"image/png"`
   * - Wildcards: `"image/*"`, `"video/*"`
   * - Extensions: `".pdf"`, `".jpg"`
   *
   * Multiple tokens separated by commas. Missing/empty means "anything".
   */
  accept?: string;
  /** Maximum file size in bytes. Missing means "no size limit". */
  maxBytes?: number;
  /**
   * Fired when a picked/dropped file fails validation. `onChange` is **not**
   * called in that case — the consumer is responsible for any user-facing
   * error message (e.g. a toast). `reason` tells you which check failed so
   * you can pick the right copy.
   */
  onReject?: (file: File, reason: FileRejectReason) => void;
  /** Disables click, keyboard activation, drag-drop, and the remove button. */
  disabled?: boolean;

  /**
   * Remote URL of an already-persisted image to preview when no local `File`
   * is selected (e.g. editing a record that was saved with an uploaded image).
   * A freshly picked `File` (`value`) always takes precedence. If the image
   * fails to load, a "no preview available" fallback is shown instead of a
   * broken image. Pass `null`/`undefined` for records without a stored image.
   */
  initialUrl?: string | null;
  /** Copy shown when `initialUrl` is set but the image fails to load. */
  noPreviewLabel?: ReactNode;
  /** Label for the replace button shown over a remote (`initialUrl`) preview. */
  replaceLabel?: ReactNode;
  /** Accessibility label for the replace button in the remote preview state. */
  replaceAriaLabel?: string;
  /**
   * Fired when the user clears the existing remote (`initialUrl`) image. Only
   * when provided does the remote preview render a "Remove" button — consumers
   * that can't clear a persisted image (no provider) simply don't pass it.
   * The consumer is responsible for dropping the underlying key from its own
   * state (which clears `initialUrl` and returns the empty dropzone).
   */
  onRemoveInitial?: () => void;
  /** Label for the remove button in the remote preview state. */
  removeInitialLabel?: ReactNode;
  /** Accessibility label for the remove button in the remote preview state. */
  removeInitialAriaLabel?: string;

  /** Heading shown in the empty state. */
  heading?: ReactNode;
  /** Supporting hint shown under the heading. */
  hint?: ReactNode;
  /** Label for the explicit upload button. */
  buttonLabel?: ReactNode;
  /** Icon shown in the empty state. */
  icon?: ReactNode;

  /** Accessibility label for the hidden `<input type="file">`. */
  inputAriaLabel?: string;
  /** Accessibility label for the remove (×) button in the preview state. */
  removeAriaLabel?: string;

  className?: string;
  "data-testid"?: string;
};

const DEFAULTS = {
  heading: "Drag and drop",
  hint: "Drag and drop a file or click to upload",
  buttonLabel: "Upload file",
  inputAriaLabel: "File upload",
  removeAriaLabel: "Remove file",
  noPreviewLabel: "No preview available",
  replaceLabel: "Replace",
  replaceAriaLabel: "Replace image",
  removeInitialLabel: "Remove",
  removeInitialAriaLabel: "Remove image",
};

/**
 * Derives a human-readable name from a remote image URL (the last path
 * segment, query string stripped) for the remote-preview header. Falls back
 * to a neutral label when the URL can't be parsed.
 */
const getFileNameFromUrl = (url: string): string => {
  try {
    const path = new URL(url, "http://_").pathname;
    const segment = decodeURIComponent(path.split("/").filter(Boolean).pop() ?? "");
    return segment || "Uploaded image";
  } catch {
    return "Uploaded image";
  }
};

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|svg)$/i;

/** MIME-first, with an extension fallback for browsers that leave `type` blank
 *  (common for SVG drops). */
const isImage = (file: File) =>
  file.type.startsWith("image/") || IMAGE_EXTENSIONS.test(file.name);
const isPdf = (file: File) => file.type === "application/pdf";

const subTestId = (root: string | undefined, suffix: string) =>
  root ? `${root}-${suffix}` : undefined;

/**
 * Matches a file against the standard HTML `accept` grammar:
 * - `.ext` — extension match (case-insensitive)
 * - `type/*` — MIME prefix wildcard
 * - `type/subtype` — exact MIME match
 *
 * No `accept` (undefined/empty) is treated as "allow anything", matching
 * the native `<input>` behaviour.
 */
const fileMatchesAccept = (file: File, accept: string | undefined): boolean => {
  if (!accept) return true;
  const tokens = accept
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;

  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();

  return tokens.some((token) => {
    if (token.startsWith(".")) return fileName.endsWith(token);
    if (token.endsWith("/*")) {
      const prefix = token.slice(0, token.length - 1); // keep the trailing slash
      return fileType.startsWith(prefix);
    }
    return fileType === token;
  });
};

const isWithinSize = (file: File, maxBytes: number | undefined): boolean =>
  maxBytes === undefined || file.size <= maxBytes;

const checkFile = (
  file: File,
  accept: string | undefined,
  maxBytes: number | undefined,
): FileRejectReason | null => {
  if (!fileMatchesAccept(file, accept)) return "type";
  if (!isWithinSize(file, maxBytes)) return "size";
  return null;
};

/**
 * `FileDropzone` — controlled single-file uploader. Shows a drag-and-drop
 * area when empty, and an inline preview (image, PDF iframe, or fallback
 * card) once a file is selected. The consumer owns the file via `value` /
 * `onChange` — this primitive holds only the transient drag-highlight state
 * and the in-memory preview URL.
 *
 *   const [file, setFile] = useState<File | null>(null);
 *   <FileDropzone value={file} onChange={setFile} accept="application/pdf" />
 *
 * All visible copy (heading, hint, button label, ARIA labels) is overridable
 * via props so consumers can wire it to their i18n layer.
 */
function FileDropzone({
  value,
  onChange,
  accept,
  maxBytes,
  onReject,
  disabled = false,
  initialUrl,
  noPreviewLabel = DEFAULTS.noPreviewLabel,
  replaceLabel = DEFAULTS.replaceLabel,
  replaceAriaLabel = DEFAULTS.replaceAriaLabel,
  onRemoveInitial,
  removeInitialLabel = DEFAULTS.removeInitialLabel,
  removeInitialAriaLabel = DEFAULTS.removeInitialAriaLabel,
  heading = DEFAULTS.heading,
  hint = DEFAULTS.hint,
  buttonLabel = DEFAULTS.buttonLabel,
  icon = <InboxOutlined />,
  inputAriaLabel = DEFAULTS.inputAriaLabel,
  removeAriaLabel = DEFAULTS.removeAriaLabel,
  className = "",
  "data-testid": dataTestId,
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  // Tracks a failed remote-image load so we can swap the broken <img> for a
  // "no preview available" fallback. Reset whenever `initialUrl` changes so a
  // new key gets a fresh load attempt.
  const [remoteErrored, setRemoteErrored] = useState(false);
  useEffect(() => {
    setRemoteErrored(false);
  }, [initialUrl]);
  const inputRef = useRef<HTMLInputElement>(null);
  // Drag events bubble through nested children; track depth so a child
  // entering/leaving doesn't flicker the highlight off prematurely.
  const dragDepthRef = useRef(0);

  // Object URLs leak memory if not revoked. Build one per file inside the
  // same effect that revokes it, so the create/revoke pair always lines up
  // — including under React 18 StrictMode's intentional mount → unmount →
  // re-mount cycle in dev. A previous `useMemo`-based variant produced a
  // URL that survived the StrictMode unmount, got revoked by the effect
  // cleanup, and was then never regenerated (deps unchanged), leaving the
  // <img> with a dead `src` and rendering as a broken preview every time
  // a saved file came back through `value` (Edit / Duplicate flows).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return undefined;
    }
    const blobUrl = URL.createObjectURL(value);
    setPreviewUrl(blobUrl);
    return () => URL.revokeObjectURL(blobUrl);
  }, [value]);

  const openPicker = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.files?.[0] ?? null;
      if (next) {
        const reason = checkFile(next, accept, maxBytes);
        if (reason) onReject?.(next, reason);
        else onChange(next);
      }
      // Reset so picking the same file again still fires `change`.
      event.target.value = "";
    },
    [accept, maxBytes, onChange, onReject],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openPicker();
      }
    },
    [disabled, openPicker],
  );

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    },
    [disabled],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
    },
    [disabled],
  );

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDragging(false);
    },
    [disabled],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);
      const dropped = event.dataTransfer.files?.[0] ?? null;
      if (!dropped) return;
      // The native `accept` attribute only filters the picker, not drops —
      // so re-check here so dragging an unrelated file in still gets blocked.
      const reason = checkFile(dropped, accept, maxBytes);
      if (reason) onReject?.(dropped, reason);
      else onChange(dropped);
    },
    [accept, maxBytes, disabled, onChange, onReject],
  );

  const handleRemove = useCallback(() => {
    if (disabled) return;
    onChange(null);
  }, [disabled, onChange]);

  return (
    <div
      className={`${styles.root} ${className}`.trim()}
      data-testid={dataTestId}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        className={styles.hiddenInput}
        aria-label={inputAriaLabel}
        onChange={handleInputChange}
        data-testid={subTestId(dataTestId, "input")}
      />

      {value && previewUrl ? (
        <div
          className={styles.preview}
          data-testid={subTestId(dataTestId, "preview")}
        >
          <div className={styles.previewHeader}>
            <span
              className={styles.previewName}
              data-testid={subTestId(dataTestId, "file-name")}
            >
              {value.name}
            </span>
            <button
              type="button"
              className={styles.previewRemove}
              onClick={handleRemove}
              disabled={disabled}
              aria-label={removeAriaLabel}
              data-testid={subTestId(dataTestId, "remove")}
            >
              <CloseOutlined />
            </button>
          </div>
          {isImage(value) ? (
            <img
              src={previewUrl}
              alt={value.name}
              className={styles.previewImage}
              data-testid={subTestId(dataTestId, "preview-image")}
            />
          ) : isPdf(value) ? (
            <iframe
              src={previewUrl}
              title={value.name}
              className={styles.previewFrame}
              data-testid={subTestId(dataTestId, "preview-frame")}
            />
          ) : (
            <div
              className={styles.previewFallback}
              data-testid={subTestId(dataTestId, "preview-fallback")}
            >
              <p className={styles.previewFallbackTitle}>{value.name}</p>
            </div>
          )}
        </div>
      ) : initialUrl ? (
        <div
          className={styles.preview}
          data-testid={subTestId(dataTestId, "remote-preview")}
        >
          <div className={styles.previewHeader}>
            <span
              className={styles.previewName}
              data-testid={subTestId(dataTestId, "remote-name")}
            >
              {getFileNameFromUrl(initialUrl)}
            </span>
            <div className={styles.previewActions}>
              <button
                type="button"
                className={styles.previewRemove}
                onClick={openPicker}
                disabled={disabled}
                aria-label={replaceAriaLabel}
                data-testid={subTestId(dataTestId, "replace")}
              >
                {replaceLabel}
              </button>
              {onRemoveInitial && (
                <button
                  type="button"
                  className={styles.previewRemove}
                  onClick={onRemoveInitial}
                  disabled={disabled}
                  aria-label={removeInitialAriaLabel}
                  data-testid={subTestId(dataTestId, "remove-initial")}
                >
                  {removeInitialLabel}
                </button>
              )}
            </div>
          </div>
          {remoteErrored ? (
            <div
              className={styles.previewFallback}
              data-testid={subTestId(dataTestId, "no-preview")}
            >
              <p className={styles.previewFallbackTitle}>{noPreviewLabel}</p>
            </div>
          ) : (
            <img
              src={initialUrl}
              alt={getFileNameFromUrl(initialUrl)}
              className={styles.previewImage}
              onError={() => setRemoteErrored(true)}
              data-testid={subTestId(dataTestId, "remote-image")}
            />
          )}
        </div>
      ) : (
        <div
          className={`${styles.dropzone} ${isDragging ? styles.dropzoneActive : ""}`.trim()}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          onClick={openPicker}
          onKeyDown={handleKeyDown}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-dragging={isDragging || undefined}
          data-disabled={disabled || undefined}
          data-testid={subTestId(dataTestId, "zone")}
        >
          <span className={styles.dropzoneIcon} aria-hidden="true">
            {icon}
          </span>
          <div className={styles.dropzoneText}>
            <p className={styles.dropzoneHeading}>{heading}</p>
            <p className={styles.dropzoneHint}>{hint}</p>
          </div>
          <Button
            intent="neutral"
            variant="outline"
            // Figma uses a muted grey rule on the Upload File button — the
            // outline variant pulls its border from `--pam-button-color`,
            // so this is the right knob (text colour is unaffected — it
            // stays `--COLOR-LIGHT_TEXT_COLOR`).
            color="var(--grey-600)"
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              openPicker();
            }}
            data-testid={subTestId(dataTestId, "upload")}
          >
            {buttonLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export default FileDropzone;
